import { useLayoutEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import Icon from './Icon.jsx'
import GlyphTile from './GlyphTile.jsx'
import { toggleInSet } from '../lib/sets.js'

/**
 * The macOS System Settings shape: a sidebar of icon + label rows on the left, one pane of
 * content on the right. A segmented control was the wrong pattern here — it works for two or
 * three peer views, but five of them across the full width reads as a toolbar nobody
 * designed, and it left no room for the section titles to breathe.
 *
 * `sections` is an array of { key, label, items }, each item { path, label, icon, tint, bg,
 * nested? },
 * grouped by what the sections are for — people, scheduling, the company itself — so the
 * sidebar answers "where would that setting live", which is the question somebody actually
 * arrives with. Roles are applied per item before it gets here, and a group with nothing left
 * in it is dropped rather than shown empty. `nested` marks a row that owns pages BELOW its
 * own path — without it the row goes dark the moment somebody opens one of them, which reads
 * as having navigated out of the section they are plainly still in. Group labels are skipped when only one group
 * survives, since a plain field-staff account seeing a lone "GENERAL" caption above the two
 * things it has is clutter, not information.
 *
 * On a phone the sidebar becomes a horizontally scrolling strip above the content, which is
 * what iOS does when a sidebar cannot fit.
 */
export default function SettingsLayout({ title, sections, children }) {
  const showLabels = sections.length > 1
  const location = useLocation()

  // Switching tabs is a client-side route change, not a real navigation — the browser never
  // resets scroll on its own. Left alone, clicking from a long pane (People) to a short one
  // (About) kept whatever scroll offset the long pane had, which the short pane couldn't
  // satisfy: the browser clamps it instantly, and the sidebar — sitting in the same grid row,
  // sized to whichever pane is taller — visibly snaps to a different spot. Scrolling to the
  // top on every tab change is what a full page load would have done for free.
  //
  // useLayoutEffect, not useEffect: the latter fires after the browser has already painted
  // the new pane at the OLD scroll offset, so the correction itself becomes a second, visible
  // jump a frame later. Doing it before paint is what makes it invisible.
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <div className="page settings-page">
      <div className="page-head">
        <h1>{title}</h1>
      </div>

      <div className="settings-shell">
        <nav className="settings-nav">
          {sections.map((section) => (
            <div className="settings-nav-group" key={section.key}>
              {showLabels && <div className="settings-nav-label">{section.label}</div>}
              {section.items.map(({ path, label, icon: ItemIcon, tint, bg, nested }) => (
                <NavLink
                  key={path}
                  to={path}
                  end={!nested}
                  className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`}
                >
                  {/* A filled colour tile rather than a bare glyph, which is the detail that
                      makes a list of ten rows scannable without reading any of them. */}
                  <GlyphTile icon={ItemIcon} tint={tint} bg={bg} />
                  <span className="nav-label">{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="settings-content">{children}</div>
      </div>
    </div>
  )
}

/**
 * Section header with the primary action pinned right. Reading order stays title → context →
 * action, and the action lands where the eye already is after scanning the row.
 */
export function CardHead({ title, sub, action }) {
  return (
    <div className="card-head">
      <div className="card-head-text">
        <h2>{title}</h2>
        {sub && <p className="sub">{sub}</p>}
      </div>
      {action && <div className="card-head-action">{action}</div>}
    </div>
  )
}

/**
 * A filter for the table below it, pinned right so it sits over the column it is narrowing
 * rather than competing with the section title on the left. Purely client-side — these
 * tables are a company's staff and job types, hundreds at the very most, and a round trip
 * per keystroke would be slower than the filter it is running.
 */
export function TableSearch({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="table-search">
      <Icon name="search" size={15} />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button type="button" className="table-search-clear" aria-label="Clear" onClick={() => onChange('')}>
          <Icon name="close" size={13} strokeWidth={2.2} />
        </button>
      )}
    </div>
  )
}

/** iOS grouped-list row: label and help text left, control right, hairline between rows. */
export function SettingsRow({ label, hint, children, stacked = false }) {
  return (
    <div className={`settings-row ${stacked ? 'stacked' : ''}`}>
      <div className="settings-row-label">
        <label>{label}</label>
        {hint && <span className="hint">{hint}</span>}
      </div>
      <div className="settings-row-control">{children}</div>
    </div>
  )
}

const DAYS = [
  [1, 'M', 'Monday'],
  [2, 'T', 'Tuesday'],
  [3, 'W', 'Wednesday'],
  [4, 'T', 'Thursday'],
  [5, 'F', 'Friday'],
  [6, 'S', 'Saturday'],
  [7, 'S', 'Sunday'],
]

/** Seven pills instead of seven checkboxes-with-labels. Reads as one control. */
export function WeekdayPicker({ value = [], onChange }) {
  const selected = new Set((value || []).map(Number))
  const toggle = (d) => onChange([...toggleInSet(selected, d)].sort((a, b) => a - b))
  return (
    <div className="weekday-picker">
      {DAYS.map(([n, letter, name]) => (
        <button
          key={n}
          type="button"
          className={selected.has(n) ? 'on' : ''}
          onClick={() => toggle(n)}
          aria-pressed={selected.has(n)}
          // The letter is what a sighted person reads; the name is what a screen reader
          // announces. Two of the letters are T and two are S, so on the letter alone the
          // control was "T, pressed" twice over with nothing to tell Tuesday from Thursday.
          aria-label={name}
          title={name}
        >
          {letter}
        </button>
      ))}
    </div>
  )
}

/**
 * `label` names the switch for a screen reader, which otherwise hears only "checkbox, on":
 * the text beside a switch is usually in a separate element, not a label it is tied to.
 */
export function Switch({ checked, onChange, disabled = false, label }) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        aria-label={label}
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="track" />
    </label>
  )
}
